import {
  CaseShippingProvider,
  CaseMilestone,
  CasePhase,
  CaseState,
  CurrencyPeriod,
  Degree,
  EmploymentBasedCategory,
  FileType,
  MaritalStatus,
  PassportGender,
  Position,
  PreparedFormState,
  ProcessRequestLocationType,
  ShipOriginalApprovalNotices,
  TaskCategory,
  TechnologyReleaseLicenseRequirement,
  UnitType,
  VisaStatus,
  VisaSubCategory,
  VisaAdditionalProcessing,
  EmployeesEmailContactDirectly,
  CompanyNameShowToFN,
  ComplianceCheckList,
  ReceiptType,
  CompanyAddressType,
  AOSFilingEligibility,
  GlobalProcessDuration,
  GlobalVisaType,
  VendorType,
} from 'API';
import {
  ExpenseDependentPetitions,
  CompanyEmployee,
  Currency,
  FileCategory,
  PaymentPreference,
  PermissionReleasePetitionsRfe,
  PreferredShippingSpeedForDocuments,
  ShippingPaymentPreference,
  UtilizePremiumProcessingService,
  EducationEvaluation,
  Role,
} from 'models';
import { FIRM_COMPANY } from 'services/firm';

/* eslint-disable import/prefer-default-export */
export enum Status {
  ERROR = 'ERROR',
  WARN = 'WARN',
  OK = 'OK',
}

export enum LoginStage {
  SIGN_IN,
  REQUEST_RESET,
  RESET_SENT,
  FORCE_PASSWORD,
  VERIFY_EMAIL,
  IS_EMAIL_VERIFIED,
}

export const resolvers = {
  CaseMilestone: (e: CaseMilestone) => {
    switch (e) {
      case CaseMilestone.APPLICATION_DECISION:
        return 'Application Decision';
      case CaseMilestone.APPLICATION_SHIPPED:
        return 'Application Shipped';
      case CaseMilestone.APPROVAL_NOTICE_SHIPPED:
        return 'Approval Notice Shipped';
      case CaseMilestone.CASE_OPENED:
        return 'Case Opened';
      case CaseMilestone.DOCUMENTS_UPLOADED:
        return 'Documents Uploaded';
      case CaseMilestone.EXPERIENCE_LETTERS_UPLOADED:
        return 'Experience Letters Uploaded';
      case CaseMilestone.FORMS_COMPLETED:
        return 'Forms Completed';
      case CaseMilestone.FORMS_PREPARED:
        return 'Forms Prepared';
      case CaseMilestone.I140_APPLICATION_SHIPPED:
        return 'I-140 Application Shipped';
      case CaseMilestone.I140_CASE_OPENING:
        return 'I-140 Case Opening';
      case CaseMilestone.I140_FORMS_PREPARED:
        return 'I-140 Forms Prepared';
      case CaseMilestone.I140_RECEIPT_NOTICE_RECEIVED:
        return 'I-140 Receipt Notice Received';
      case CaseMilestone.JOB_DESCRIPTION_FINALIZED:
        return 'Job Description Finalized';
      case CaseMilestone.LCA_FILED:
        return 'LCA Filed';
      case CaseMilestone.LCA_RECEIVED:
        return 'LCA Received';
      case CaseMilestone.NEXT_STEPS:
        return 'Next Steps';
      case CaseMilestone.PERM_CERTIFIED:
        return 'PERM Certified';
      case CaseMilestone.PERM_PREPARED:
        return 'PERM Prepared';
      case CaseMilestone.PERM_REVIEWED:
        return 'PERM Reviewed';
      case CaseMilestone.PERM_SUBMITED:
        return 'PERM Submitted';
      case CaseMilestone.PREVAILING_WAGE_REQUEST_FILED:
        return 'Prevailing Wage Determination Filed';
      case CaseMilestone.PREVAILING_WAGE_DETERMINATION_RECEIVED:
        return 'Prevailing Wage Determination Received';
      case CaseMilestone.QUESTIONNAIRE_COMPLETED:
        return 'Questionnaire Completed';
      case CaseMilestone.RECEIPT_NOTICE_RECEIVED:
        return 'Receipt Notice Received';
      case CaseMilestone.RECRUITMENT_BEGUN:
        return 'Recruitment Begun';
      case CaseMilestone.RECRUITMENT_COMPLETE:
        return 'Recruitment Complete';
      case CaseMilestone.PREPARE_DS_160:
        return 'Prepare DS-160';
      case CaseMilestone.COMPLETE_DS_160:
        return 'Complete DS-160';
      case CaseMilestone.MH_PREP_PHONE_CALL:
        return 'MH Prep Phone Call';
      case CaseMilestone.SHIP_DECISION_NOTICE:
        return 'Ship Decision Notice';
      case CaseMilestone.LOTTERY_PREPARATION:
        return 'Lottery Preparation';
      case CaseMilestone.LOTTERY_SUBMISSION:
        return 'Lottery Submission';
      case CaseMilestone.LOTTERY_DECISION:
        return 'Lottery Decision';
      case CaseMilestone.LOTTERY_NOTIFICATION:
        return 'Lottery Notification';
      case CaseMilestone.SHIPMENT_APPROVAL_NOTICE:
        return 'Shipment of Approval Notice';
      default:
        return e;
    }
  },
  CasePhase: (e: CasePhase) => {
    switch (e) {
      case CasePhase.APPLICATION_SUBMISSION:
        return 'Application Submission';

      case CasePhase.CASE_OPENING:
        return 'Case Opening';

      case CasePhase.CASE_PREPARATION:
        return 'Case Preparation';

      case CasePhase.DATA_COLLECTION:
        return 'Data Collection';

      case CasePhase.FILE_I140:
        return 'File I-140';

      case CasePhase.FILE_PREVAILING_WAGE_REQUEST:
        return 'File Prevailing Wage Request';

      case CasePhase.FINAL_DECISION:
        return 'Final Decision';

      case CasePhase.FORMS_PREPARATION:
        return 'Forms Preparation';

      case CasePhase.I140_PREPARATION:
        return 'I-140 Preparation';

      case CasePhase.LCA_FILING:
        return 'LCA Filing';
      case CasePhase.NEXT_STEPS:
        return 'Next Steps';

      case CasePhase.PERM_SUBMISSION:
        return 'PERM Submission';

      case CasePhase.RECRUITMENT:
        return 'Recruitment';

      case CasePhase.DS_160_SUBMISSION:
        return 'DS-160 Submission';

      case CasePhase.CONSULAR_PREPARATION:
        return 'Consular Preparation';

      case CasePhase.AWAITING_RECEIPT:
        return 'Awaiting Receipt';

      case CasePhase.CBP_PREPARATION:
        return 'CBP Preparation';

      case CasePhase.LOTTERY_SUBMISSION:
        return 'Lottery Submission';

      case CasePhase.LOTTERY_DECISION:
        return 'Lottery Decision';

      default:
        return e;
    }
  },
  CaseShippingProvider: (e: CaseShippingProvider) => {
    switch (e) {
      case CaseShippingProvider.FEDEX:
        return 'Fed Ex';

      case CaseShippingProvider.UPS:
        return 'UPS';

      default:
        return e;
    }
  },
  CaseState: (e: CaseState) => {
    switch (e) {
      case CaseState.CLOSED:
        return 'Closed';

      case CaseState.OPEN:
        return 'Open';

      case CaseState.COMPLETED:
        return 'Completed';

      case CaseState.PENDING:
        return 'Pending';

      case CaseState.NA:
        return 'N/A';

      default:
        return e;
    }
  },
  EmploymentBasedCategory: (e: EmploymentBasedCategory) => {
    switch (e) {
      case EmploymentBasedCategory.EB_1:
        return 'EB-1';
      case EmploymentBasedCategory.EB_2:
        return 'EB-2';
      case EmploymentBasedCategory.EB_3:
        return 'EB-3';
      case EmploymentBasedCategory.EB_4:
        return 'EB-4';
      case EmploymentBasedCategory.EB_5_NON_REGIONAL_CENTER:
        return 'EB-5 Unreserved';
      case EmploymentBasedCategory.EB_5_REGIONAL_CENTER:
        return 'EB-5 Regional Center';
      case EmploymentBasedCategory.EB_OTHER_WORKERS:
        return 'EB-Other Workers';
      case EmploymentBasedCategory.EB_CERTAIN_RELIGIOUS_WORKERS:
        return 'EB-Certain Religious Workers';
      case EmploymentBasedCategory.EB_5_SET_ASIDE:
        return 'EB-5 Set Aside';
      case EmploymentBasedCategory.SPOUSE_OF_US_CITIZEN:
        return 'Spouse of U.S. Citizen';
      case EmploymentBasedCategory.PARENT_OF_US_CITIZEN:
        return 'Parent of U.S. Citizen';
      case EmploymentBasedCategory.F_1:
        return 'F-1';
      case EmploymentBasedCategory.F_2A:
        return 'F-2A';
      case EmploymentBasedCategory.F_2B:
        return 'F-2B';
      case EmploymentBasedCategory.F_3:
        return 'F-3';
      case EmploymentBasedCategory.F_4:
        return 'F-4';
      case EmploymentBasedCategory.REFUGEE_OR_ASYLEE:
        return 'Refugee or Asylee';
      case EmploymentBasedCategory.DIVERSITY:
        return 'Diversity';
      case EmploymentBasedCategory.SPECIAL_IMMIGRANT:
        return 'Special Immigrant';
      case EmploymentBasedCategory.HUMAN_TRAFFICKING_AND_CRIME_VICTIM:
        return 'Human Trafficking and Crime Victims';
      case EmploymentBasedCategory.VICTIM_OF_ABUSE:
        return 'Victims of Abuse';
      default:
        return e;
    }
  },
  ExpenseDependentPetitions: (e: ExpenseDependentPetitions) => {
    switch (e) {
      case ExpenseDependentPetitions.BOTH:
        return 'Yes, both';

      case ExpenseDependentPetitions.NO:
        return 'No';

      case ExpenseDependentPetitions.ONLY_DEPENDENT:
        return 'Yes, only Dependent Petitions';

      case ExpenseDependentPetitions.ONLY_EAD_CARDS:
        return 'Yes, only EAD Cards';

      default:
        return e;
    }
  },
  CompanyEmployee: (e: CompanyEmployee) => {
    switch (e) {
      case CompanyEmployee.COMPANY:
        return 'Company';

      case CompanyEmployee.EMPLOYEE:
        return 'Employee';

      default:
        return e;
    }
  },
  Currency: (currency: Currency): string => {
    switch (currency) {
      case Currency.USD:
        return '$';

      case Currency.EUR:
        return '€';

      default:
        return '$';
    }
  },
  CurrencyPeriod: (period: CurrencyPeriod): string => {
    switch (period) {
      case CurrencyPeriod.ANNUAL:
        return 'per year';

      case CurrencyPeriod.QUARTERLY:

        return 'per quarter';

      case CurrencyPeriod.MONTHLY:
        return 'per month';

      case CurrencyPeriod.WEEKLY:
        return 'per week';

      case CurrencyPeriod.HOURLY:
        return 'per hour';

      default:
        return period;
    }
  },
  Degree: (e: Degree): string => {
    switch (e) {
      case Degree.NONE:
        return 'None';

      case Degree.BACHELORS:
        return 'Bachelors';

      case Degree.MASTERS:
        return 'Masters';

      case Degree.PHD:
        return 'PhD';

      default:
        return e;
    }
  },
  EducationEvaluation: (e: EducationEvaluation) => {
    switch (e) {
      case EducationEvaluation.COMPANY_WILL_PROVIDE:
        return 'Company will provide the evaluation';

      case EducationEvaluation.MH_SHOULD_GET:
        return 'Meltzer Hellrung should get the evaluation, always, and invoice the company';

      case EducationEvaluation.MH_SHOULD_REACH_OUT:
        return 'Meltzer Hellrung should reach out to the company to secure permission before requesting an evaluation from vendor';

      default:
        return e;
    }
  },
  EmployeesEmailContactDirectly: (e: EmployeesEmailContactDirectly) => {
    switch (e) {
      case EmployeesEmailContactDirectly.A_COMPANY_REPRESENTATIVE_SHOULD_BE_COPIED_ON_ALL_COMMUNICATION:
        return 'A company representative should be copied on all communication';

      case EmployeesEmailContactDirectly.A_COMPANY_REPRESENTATIVE_WILL_ONLY_BE_COPIED_ON_INFORMATION_IMPACTING_THE_COMPANY_BUT_WILL_BE_LEFT_OFF_ON_COMMUNICATION_THAT_DOES_NOT_IMPACT_THE_COMPANY:
        return 'A company representative will only be copied on information impacting the company, but will be left off on communication that does not impact the company';

      default:
        return e;
    }
  },

  FileCategory: (e: FileCategory | string) => {
    switch (e) {
      case FileCategory.COMPANY:
        return 'Company';
      case FileCategory.EDUCATION:
        return 'Education';
      case FileCategory.EMPLOYMENT:
        return 'Employment';
      case FileCategory.OTHER:
        return 'Other';
      case FileCategory.PERSONAL_IDENTIFICATION:
        return 'Personal Identification';
      case FileCategory.US_IMMIGRATION:
        return 'U.S. Immigration';
      case FileCategory.FORMS_LETTERS:
        return 'Forms + Letters';
      case FileCategory.PUBLIC_ACCESS_FILE:
        return 'Public Access File';
      case FileCategory.APPLICATIONS_APPROVALS:
      case FileCategory.APPLICATIONS:
        return 'Applications';
      case FileCategory.SIGNATURE_PAGES:
        return 'Signature Pages';
      case FileCategory.GOVERNMENT_NOTICES:
        return 'Government Notices';
      case FileCategory.PERM_RECRUITMENT:
        return 'PERM Recruitment';
      default:
        return e;
    }
  },
  FileType: (e: FileType | string, lower?: boolean) => {
    switch (e) {
      // Applications
      case FileType.I_140_APPLICATION:
        return lower ? 'I-140 application' : 'I-140 Application';

      case FileType.DS_160_COPY:
        return lower ? 'copy of DS-160' : 'Copy of DS-160';

      case FileType.DOL_AUDIT_RESPONSE:
        return lower ? 'DOL audit response' : 'DOL Audit Response';

      case FileType.E_3_APPLICATION:
        return lower ? 'E-3 application' : 'E-3 Application';

      case FileType.E_3D_APPLICATION:
        return lower ? 'E-3D application' : 'E-3D Application';

      case FileType.E_3D_CONSULAR_PACKET:
        return lower ? 'E-3D consular packet' : 'E-3D Consular Packet';

      case FileType.H_1B_APPLICATION:
        return lower ? 'H-1B application' : 'H-1B Application';

      case FileType.H_1B1_APPLICATION:
        return lower ? 'H-1B1 application' : 'H-1B1 Application';

      case FileType.H_4_CONSULAR_PACKET:
        return lower ? 'H-4 consular packet' : 'H-4 Consular Packet';

      case FileType.PERM_APPLICATION:
        return lower ? 'PERM application' : 'PERM Application';

      case FileType.REQUEST_EVIDENCE_RESPONSE:
        return lower ? 'request for evidence response' : 'Request for Evidence Response';

      case FileType.TD_APPLICATION:
        return lower ? 'TD application' : 'TD Application';

      case FileType.TD_BORDER_PACKET:
        return lower ? 'TD border packet' : 'TD Border Packet';

      case FileType.TD_CONSULAR_PACKET:
        return lower ? 'TD consular packet' : 'TD Consular Packet';

      case FileType.TN_APPLICATION:
        return lower ? 'TN application' : 'TN Application';

      case FileType.APPLICATION_COPY_EMPLOYEE:
        return lower ? 'application copy - employee' : 'Application Copy - Employee';

      case FileType.USCIS_PRIMARY_APPLICATION:
        return lower ? 'USCIS primary application' : 'USCIS Primary Application';

      case FileType.USCIS_DEPENDENT_APPLICATION:
        return lower ? 'USCIS dependent application' : 'USCIS Dependent Application';

        // Company
      case FileType.COMPANY_FINANCIAL_STATEMENT:
        return lower ? 'company financial statement' : 'Company Financial Statement';

      case FileType.MARKETING_MATERIAL:
        return lower ? 'marketing materials' : 'Marketing Materials';

      case FileType.ATTORNEY_CLIENT_AGREEMENT:
        return lower ? 'attorney-client agreement' : 'Attorney-Client Agreement';

        // Education
      case FileType.DIPLOMA:
        return lower ? 'diploma(s), degree(s)' : 'Diploma(s), Degree(s)';

      case FileType.SCHOOL_MARK_TRANSCRIPT:
        return lower ? 'school marks and transcripts' : 'School Marks and Transcripts';

      case FileType.EDUCATION_EVALUATION:
        return lower ? 'educational evaluations' : 'Educational Evaluations';

        // Employment
      case FileType.JOB_DESCRIPTION:
        return lower ? 'job description' : 'Job Description';

      case FileType.PAY_STUBS:
        return lower ? 'pay stubs' : 'Pay Stubs';

        // Forms + Letters
      case FileType.COMPLETED_FORMS:
        return lower ? 'completed forms' : 'Completed Forms';

      case FileType.FLATTENED_FORMS:
        return lower ? 'flattened documents & forms' : 'Flattened Documents & Forms';

      case FileType.RFGP_FINAL_SUBMISSION:
        return lower ? 'RFGP (final submission)' : 'RFGP (Final Submission)';

      case FileType.EXPERIENCE_LETTER:
        return lower ? 'experience letter' : 'Experience Letter';

        // Government Notices
      case FileType.I_140_RECEIPT_NOTICE:
        return lower ? 'I-140 receipt notice' : 'I-140 Receipt Notice';

      case FileType.I_140_APPROVAL_NOTICE:
        return lower ? 'I-140 approval notice' : 'I-140 Approval Notice';

      case FileType.APPROVAL_NOTICE:
        return lower ? 'approval notice' : 'Approval Notice';

      case FileType.CERTIFIED_PERM:
        return lower ? 'certified PERM' : 'Certified PERM';

      case FileType.DOL_AUDIT_NOTICE:
        return lower ? 'DOL audit notice' : 'DOL Audit Notice';

      case FileType.OTHER_NOTICES:
        return lower ? 'other notices' : 'Other Notices';

      case FileType.RECEIPT_NOTICE:
        return lower ? 'receipt notice' : 'Receipt Notice';

      case FileType.REQUEST_FOR_EVIDENCE:
        return lower ? 'request for evidence' : 'Request for Evidence';

        // Other
      case FileType.OTHER:
        return lower ? 'other' : 'Other';

        // Personal Identification
      case FileType.BIRTH_CERTIFICATE:
        return lower ? 'birth certificate' : 'Birth Certificate';

      case FileType.MARRIAGE_CERTIFICATE:
        return lower ? 'marriage certificate' : 'Marriage Certificate';

      case FileType.PASSPORT:
        return lower ? 'passport' : 'Passport';

      case FileType.PASSPORT_PHOTO:
        return lower ? 'passport-style photo' : 'Passport-Style Photo';

      case FileType.RESUME:
        return lower ? 'resume' : 'Resume';

        // Public Access File
      case FileType.ACTUAL_PREVAILING_WAGE_MEMO:
        return lower ? 'actual & prevailing wage memo' : 'Actual & Prevailing Wage Memo';

      case FileType.LABOR_CONDITION_APPLICATION_NOTICE_FILLING:
        return lower ? 'notice of filing' : 'Notice of Filing';

      case FileType.OES_WAGES:
        return lower ? 'OES wages' : 'OES Wages';

      case FileType.PAF_TEMPLATES:
        return lower ? 'PAF templates' : 'PAF Templates';

      case FileType.SIGNED_CERTIFIED_LCA:
        return lower ? 'signed sertified sabor condition application (LCA)' : 'Signed Certified Labor Condition Application (LCA)';

      case FileType.SIGNED_NOTICE_FILING_CONFIRMATION:
        return lower ? 'signed notice of filing confirmation' : 'Signed Notice of Filing Confirmation';

      case FileType.SUMMARY_OF_BENEFITS:
        return lower ? 'summary of benefits' : 'Summary of Benefits';

        // Signature Pages
      case FileType.DEPENDENT_VISA_USCIS_SIGNATURE_PAGES:
        return lower ? 'dependent visa USCIS signature pages' : 'Dependent Visa USCIS Signature Pages';

      case FileType.EMPLOYMENT_VISA_CONSULAR_SIGNATURE_PAGES:
        return lower ? 'employment visa consular signature pages' : 'Employment Visa Consular Signature Pages';

      case FileType.EMPLOYMENT_VISA_USCIS_SIGNATURE_PAGES:
        return lower ? 'employment visa USCIS signature pages' : 'Employment Visa USCIS Signature Pages';

      case FileType.PERM_SIGNATURE_PAGES:
        return lower ? 'PERM signature pages' : 'PERM Signature Pages';

        // U.S Immigration
      case FileType.EAD_CARD:
        return lower ? 'EAD card' : 'EAD Card';

      case FileType.EVIDENCE_OF_PRIMARY_E3:
        return lower ? "evidence of primary E-3 holder's lawful U.S. presence" : "Evidence of Primary E-3 Holder's Lawful U.S. Presence";

      case FileType.EVIDENCE_OF_PRIMARY_H1B:
        return lower ? "evidence of primary H-1B holder's lawful U.S. presence" : "Evidence of Primary H-1B Holder's Lawful U.S. Presence";

      case FileType.EVIDENCE_OF_PRIMARY_TN:
        return lower ? "evidence of primary TN holder's lawful U.S. presence" : "Evidence of Primary TN Holder's Lawful U.S. Presence";

      case FileType.I_20_COPY:
        return lower ? 'I-20 copies' : 'I-20 Copies';

      case FileType.I_797_APPROVAL_NOTICE:
        return lower ? 'I-797 approval notice' : 'I-797 Approval Notice';

      case FileType.I_94:
        return lower ? 'I-94 from most recent U.S. entry' : 'I-94 from Most Recent U.S. Entry';

      case FileType.PREVIOUS_I_140_APPROVAL_NOTICES:
        return lower ? 'previous I-140 approval notices' : 'Previous I-140 Approval Notices';

      case FileType.PREVIOUS_I_797_APPROVAL_NOTICES:
        return lower ? 'previous I-797 approval notices' : 'Previous I-797 Approval Notices';

      case FileType.PRIOR_EAD:
        return lower ? 'prior EAD' : 'Prior EAD';

      case FileType.VISA_STAMPS:
        return lower ? 'visa stamp(s)' : 'Visa Stamp(s)';

      case FileType.EVIDENCE_OF_PRIMARY_USCIS:
        return lower ? 'evidence of primary USCIS visa holder\'s lawful U.S. presence' : 'Evidence of Primary USCIS Visa Holder\'s Lawful U.S. Presence';

        // Extra Document Resolvers
      case FileType.H_4_APPLICATION:
        return lower ? 'H-4 application' : 'H-4 Application';

      case FileType.MASTER_AGREEMENT:
        return lower ? 'master services agreement and/or statement of work for end-client assignment' : 'Master Services Agreement and/or Statement of Work for End-Client Assignment';

      case FileType.SIGNED_EMPLOYMENT_CONTRACT:
        return lower ? 'signed employment contract' : 'Signed Employment Contract';

      case FileType.SUPPORT_LETTER:
        return lower ? 'letter of support' : 'Letter of Support';

      case FileType.VENDOR_CLIENT_LETTERS:
        return lower ? 'mid-vendor and end-client letters' : 'Mid-Vendor and End-Client Letters';

      case FileType.W2:
        return lower ? 'most recent W-2' : 'Most Recent W-2';

      case FileType.CERTIFIED_LABOR_CONDITION_APPLICATION:
        return lower ? 'certified labor condition application (LCA)' : 'Certified Labor Condition Application (LCA)';

        // Extra Documents
      case FileType.H_1B_RECEIPT_NOTICE:
        return lower ? 'H-1B receipt notice' : 'H-1B Receipt Notice';

      case FileType.H_1B_SIGNATURE_PAGES:
        return lower ? 'H-1B signature pages' : 'H-1B Signature Pages';

      case FileType.H_4_RECEIPT_NOTICE:
        return lower ? 'H-4 receipt notice' : 'H-4 Receipt Notice';

      case FileType.H_4_SIGNATURE_PAGES:
        return lower ? 'H-4 signature pages' : 'H-4 Signature Pages';

      case FileType.PRIOR_I_20:
        return lower ? 'prior I-20s, if applicable' : 'Prior I-20s, if Applicable';

      case FileType.BIRTH_CERTIFICATE_OR_MARRIAGE_CERTIFICATE:
        return lower ? 'birth certificate or marriage certificate' : 'Birth Certificate or Marriage Certificate';

      case FileType.H_1B1_SIGNATURE_PAGES:
        return lower ? 'H-1B1 signatore pages' : 'H-1B1 Signature Pages';

      case FileType.H_1B1_APPROVAL_NOTICE:
        return lower ? 'H-1B1 approval notice' : 'H-1B1 Approval Notice';

      case FileType.H_1B1_RECEIPT_NOTICE:
        return lower ? 'H-1B1 receipt notice' : 'H-1B1 Receipt Notice';

      case FileType.EAD_COPIES:
        return lower ? 'EAD copies' : 'EAD Copies';

      case FileType.STATEMENT_OF_WORK:
        return lower ? 'statement of work' : 'Statement of Work';

      case FileType.PREMIUM_PROCESSING_UPGRADE_FORMS:
        return lower ? 'premium processing upgrade forms' : 'Premium Processing Upgrade Forms';

      case FileType.OTHER_PUBLIC_ACCESS_FILE:
        return lower ? 'other' : 'Other';

      case FileType.EAD_SIGNATURE_PAGES:
        return lower ? 'EAD signature pages' : 'EAD Signature Pages';

      case FileType.EAD_APPLICATION:
        return lower ? 'EAD application' : 'EAD Application';

      case FileType.H1B_LOTTERY_SELECTION_NOTICE:
        return lower ? 'H-1B lottery selection notice' : 'H-1B Lottery Selection Notice';

      // PERM Recruitment
      case FileType.CANDIDATE_RESUMES_AND_ASSESSMENTS:
        return lower ? 'candidate resumes and assessments' : 'Candidate Resumes and Assessments';

      case FileType.SIGNED_COPY_OF_NOTICE_OF_FILLING_NOF:
        return lower ? 'signed copy of notice of filing (NOF)' : 'Signed Copy of Notice of Filing (NOF)';

      case FileType.EMPLOYEE_REFERRAL_PROGRAM:
        return lower ? 'employee referral program' : 'Employee Referral Program';

      case FileType.SUNDAY_NEWSPAPER_AD_1:
        return lower ? 'sunday newspaper ad #1' : 'Sunday Newspaper Ad #1';

      case FileType.SUNDAY_NEWSPAPER_AD_2:
        return lower ? 'sunday newspaper ad #2' : 'Sunday Newspaper Ad #2';

      case FileType.STATE_WORKFORCE_AGENCY_POSTING:
        return lower ? 'state workforce agency posting' : 'State Workforce Agency Posting';

      case FileType.WEEKDAY_ETHNIC_NEWSPAPER_AD:
        return lower ? 'weekday/ethnic newspaper Ad' : 'Weekday/Ethnic Newspaper Ad';

      case FileType.RADIO_AD:
        return lower ? 'radio ad' : 'Radio Ad';

      case FileType.CAMPUS_PLACEMENT:
        return lower ? 'campus placement' : 'Campus Placement';

      case FileType.JOB_FAIR:
        return lower ? 'job fair' : 'Job Fair';

      case FileType.COMPANY_WEBSITE:
        return lower ? 'company website' : 'Company Website';

      case FileType.JOB_SEARCH_ENGINE:
        return lower ? 'job search engine' : 'Job Search Engine';

      case FileType.GREEN_CARD:
        return lower ? 'green card' : 'Green Card';

      case FileType.PREVAILING_WAGE_DETERMINATION:
        return lower ? 'prevailing wage determination' : 'Prevailing Wage Determination';

      case FileType.ADVANCED_PAROLE:
        return lower ? 'advanced parole' : 'Advanced Parole';

      case FileType.RE_ENTRY_PERMIT:
        return lower ? 're-entry permit' : 'Re-Entry Permit';

      case FileType.DS_2019:
        return lower ? 'ds-2019' : 'DS-2019';

      default:
        return e;
    }
  },
  MaritalStatus: (e: MaritalStatus) => {
    switch (e) {
      case MaritalStatus.UNMARRIED:
        return 'Unmarried';

      case MaritalStatus.MARRIED:
        return 'Married';

      case MaritalStatus.DIVORCED:
        return 'Divorced';

      case MaritalStatus.WIDOWED:
        return 'Widowed';

      default:
        return e;
    }
  },
  PassportGender: (e: PassportGender) => {
    switch (e) {
      case PassportGender.FEMALE:
        return 'Female';

      case PassportGender.MALE:
        return 'Male';

      case PassportGender.OTHER:
        return 'Other';

      default:
        return e;
    }
  },
  CompanyPreferredMailingAddress: (e: CompanyAddressType) => {
    switch (e) {
      case CompanyAddressType.HEADQUARTERS:
        return 'Headquarters';

      case CompanyAddressType.SECONDARY:
        return 'Secondary';

      default:
        return e;
    }
  },
  PaymentPreference: (e: PaymentPreference) => {
    switch (e) {
      case PaymentPreference.CHECK:
        return 'Check';

      case PaymentPreference.ACH:
        return 'ACH';

      case PaymentPreference.CREDIT_CARD: {
        return 'Credit card';
      }

      default:
        return e;
    }
  },
  PermissionReleasePetitionsRFE: (e: PermissionReleasePetitionsRfe) => {
    switch (e) {
      case PermissionReleasePetitionsRfe.TRUE:
        return 'Yes';

      case PermissionReleasePetitionsRfe.FALSE:
        return 'No';

      case PermissionReleasePetitionsRfe.YES_WITH_COMPANY_PERMISSION: {
        return 'Yes, but only after seeking permission from company first';
      }

      default:
        return e;
    }
  },
  Position: (e: Position) => {
    switch (e) {
      case Position.ATTORNEY:
        return 'Attorney';

      case Position.CONTRIBUTOR:
        return 'Contributor';

      case Position.LEGAL_ASSISTANT:
        return 'Legal assistant';

      case Position.PARALEGAL:
        return 'Paralegal';

      default:
        return e;
    }
  },
  PreferredShippingSpeedForDocuments: (
    e: PreferredShippingSpeedForDocuments,
  ) => {
    switch (e) {
      case PreferredShippingSpeedForDocuments.OVERNIGHT:
        return 'Overnight';

      case PreferredShippingSpeedForDocuments.TWO_DAYS:
        return 'Two days';

      case PreferredShippingSpeedForDocuments.SAVER: {
        return 'Saver';
      }

      default:
        return e;
    }
  },
  PreparedFormState: (e: PreparedFormState) => {
    switch (e) {
      case PreparedFormState.TODO:
        return 'Ready to begin';

      case PreparedFormState.AWAITING_NATIONAL_APPROVAL:
        return 'Awaiting national approval';

      case PreparedFormState.COMPLETED:
        return 'Completed';
      default:
        return e;
    }
  },
  ProcessRequestLocationType: (e: ProcessRequestLocationType) => {
    switch (e) {
      case ProcessRequestLocationType.HEADQUARTERS:
        return 'Company Headquarters';

      case ProcessRequestLocationType.SECONDARY:
        return 'Company Secondary Location';

      case ProcessRequestLocationType.REMOTE:
        return 'Remote';

      default:
        return e;
    }
  },
  Role: (role: Role, plural?: boolean) => {
    switch (role) {
      case Role.ADMIN:
        return plural ? 'Team Member Admins' : 'Team Member Admin';

      case Role.EMPLOYER:
        return plural ? 'Employers' : 'Employer';

      case Role.FOREIGNNATIONAL:
        return plural ? 'Foreign Nationals' : 'Foreign National';

      case Role.TEAMMEMBER:
        return plural ? 'Team Members' : 'Team Member';

      default:
        return role;
    }
  },
  ComplianceCheckListResolver: (option: ComplianceCheckList) => {
    switch (option) {
      case ComplianceCheckList.NO:
        return 'No';
      case ComplianceCheckList.YES:
        return 'Yes';
      case ComplianceCheckList.NOT_APPLICABLE:
        return 'Not Applicable';
      default:
        return option;
    }
  },
  ShipOriginalApprovalNotices: (e: ShipOriginalApprovalNotices) => {
    switch (e) {
      case ShipOriginalApprovalNotices.COMPANY:
        return 'Company';

      case ShipOriginalApprovalNotices.EMPLOYEE:
        return 'Employee';

      case ShipOriginalApprovalNotices.HOLD:
        return `Hold with ${FIRM_COMPANY.name}`;

      default:
        return e;
    }
  },
  ShippingPaymentPreference: (e: ShippingPaymentPreference) => {
    switch (e) {
      case ShippingPaymentPreference.FEDEX:
        return 'FedEx';

      case ShippingPaymentPreference.UPS:
        return 'UPS';

      case ShippingPaymentPreference.MH:
        return 'Pay Meltzer Hellrung for shipping services';

      default:
        return e;
    }
  },
  TaskCategory: (e: TaskCategory) => {
    switch (e) {
      case TaskCategory.CONSULAR_PROCESS:
        return 'Consular Process';
      case TaskCategory.OPENING_CASE:
        return 'Opening Case';
      case TaskCategory.LCA_FILING:
      case TaskCategory.LCA_FILING_HIDDEN:
        return 'LCA Filing';
      case TaskCategory.PUBLIC_ACCESS_FILE:
      case TaskCategory.PUBLIC_ACCESS_FILE_HIDDEN:
        return 'Public Access File';
      case TaskCategory.PREPARING_MATERIALS:
      case TaskCategory.PREPARING_MATERIALS_HIDDEN:
        return 'Preparing Materials';
      case TaskCategory.APPLICATION_REVIEW:
      case TaskCategory.APPLICATION_REVIEW_HIDDEN:
        return 'Application Review';
      case TaskCategory.SUBMITTING_APPLICATION:
      case TaskCategory.SUBMITTING_APPLICATION_HIDDEN:
        return 'Submitting Application';
      case TaskCategory.RECEIPT_BY_GOVERNMENT:
      case TaskCategory.RECEIPT_BY_GOVERNMENT_HIDDEN:
        return 'Receipt by Government';
      case TaskCategory.PREMIUM_PROCESSING_UPGRADE:
        return 'Premium Processing Upgrade';
      case TaskCategory.REQUEST_FOR_EVIDENCE:
        return 'Request for Evidence';
      case TaskCategory.FINAL_DECISION:
      case TaskCategory.FINAL_DECISION_HIDDEN:
        return 'Final Decision';
      case TaskCategory.OPENING_PERM_CASE:
        return 'Opening PERM Case';
      case TaskCategory.FINALIZE_JOB_DESCRIPTION:
        return 'Finalize Job Description';
      case TaskCategory.EXPERIENCE_LETTERS:
        return 'Experience Letters';
      case TaskCategory.PREVAILING_WAGE_REQUEST:
        return 'Prevailing Wage Request';
      case TaskCategory.RECRUITMENT:
        return 'Recruitment';
      case TaskCategory.PERM_SUBMISSION:
        return 'PERM Submission';
      case TaskCategory.DOL_AUDIT:
        return 'DOL Audit';
      case TaskCategory.PERM_CERTIFICATION:
        return 'PERM Certification';
      case TaskCategory.REQUEST_FOR_RECONSIDERATION:
        return 'Request for Reconsideration';
      case TaskCategory.OPENING_I40_CASE:
        return 'Opening I-140 Case';
      case TaskCategory.CBP_APPLICATION_PROCESS:
        return 'CBP Application Process';
      case TaskCategory.BIOMETRICS:
        return 'Biometrics';
      case TaskCategory.I_485J_PROCESS:
        return 'I-485J Process';
      case TaskCategory.USCIS_INTERVIEW:
        return 'USCIS Interview';
      case TaskCategory.MEDICAL_EXAM:
        return 'Medical Exam';
      case TaskCategory.LOTTERY_PROCESS:
        return 'Lottery Process';
      case TaskCategory.NEW_USCIS_ACCOUNT:
        return 'New USCIS Account';
      default:
        return e;
    }
  },
  TechnologyReleaseLicenseRequirement: (
    e: TechnologyReleaseLicenseRequirement,
  ) => {
    switch (e) {
      case TechnologyReleaseLicenseRequirement.NON_REQUIRED:
        return `A license is not required from either the U.S. Department of 
          Commerce or the U.S. Department of State to release such technology
          or technical data to the foreign person`;

      case TechnologyReleaseLicenseRequirement.REQUIRED:
        return `A license is required from the U.S. Department of Commerce
          and/or the U.S. Department of State to release such technology or
          technical data to the beneficiary and the petitioner will prevent
          access to the controlled technology or technical data by the
          beneficiary until and unless the petitioner has received the required
          license or other authorization to release it too the beneficiary`;

      default:
        return e;
    }
  },
  CompanyNameShowToFNResolver: (
    e: string,
  ) => {
    switch (e) {
      case CompanyNameShowToFN.LEGAL_NAME_OF_US_COMPANY:
        return 'Legal Name of U.S. Company';

      case CompanyNameShowToFN.DOING_BUSINESS_AS:
        return '"Doing Business As" Name of Company';

      default:
        return e;
    }
  },
  UnitType: (e: UnitType) => {
    switch (e) {
      case UnitType.APARTMENT:
        return 'Apartment';

      case UnitType.FLOOR:
        return 'Floor';

      case UnitType.SUITE:
        return 'Suite';

      case UnitType.NONE:
        return 'None';

      default:
        return e;
    }
  },
  UtilizePremiumProcessingService: (e: UtilizePremiumProcessingService) => {
    switch (e) {
      case UtilizePremiumProcessingService.ALWAYS_PAID:
        return 'Always, paid for by company';

      case UtilizePremiumProcessingService.CASE_BY_CASE:
        return 'Only on a case-by-case basis';

      case UtilizePremiumProcessingService.PAID_BY_EMPLOYEE:
        return 'Preference of the employee, paid for by employee';

      default:
        return e;
    }
  },
  AOSFilingEligibility: (e: AOSFilingEligibility) => {
    switch (e) {
      case AOSFilingEligibility.YES:
        return 'Yes';
      case AOSFilingEligibility.NO:
        return 'No';
      case AOSFilingEligibility.UNDETERMINED:
        return 'Undetermined';
      default:
        return e;
    }
  },
  GlobalProcessDuration: (e: GlobalProcessDuration) => {
    switch (e) {
      case GlobalProcessDuration.SHORT_TERM_BUSINESS_VISA:
        return 'Short Term Visa';
      case GlobalProcessDuration.LONG_TERM_EMPLOYMENT:
        return 'Long Term Visa';
      default:
        return e;
    }
  },
  GlobalVisaType: (e: GlobalVisaType) => {
    switch (e) {
      case GlobalVisaType.BUSINESS:
        return 'Business';
      case GlobalVisaType.TOURIST:
        return 'Tourist';
      case GlobalVisaType.CONFERENCE:
        return 'Conference';
      default:
        return e;
    }
  },
  VendorType: (e: VendorType) => {
    switch (e) {
      case VendorType.COMPANY:
        return 'Vendor';
      case VendorType.CO_COUNSEL:
        return 'Co-Counsel';
      default:
        return e;
    }
  },
  VisaStatus: (e: VisaStatus) => {
    switch (e) {
      case VisaStatus.B_1:
        return 'B-1';

      case VisaStatus.B_2:
        return 'B-2';

      case VisaStatus.E_1:
        return 'E-1';

      case VisaStatus.E_2:
        return 'E-2';

      case VisaStatus.F_1:
        return 'F-1';

      case VisaStatus.F_2:
        return 'F-2';

      case VisaStatus.H_1B:
        return 'H-1B';

      case VisaStatus.H_2A:
        return 'H-2A';

      case VisaStatus.H_1B1:
        return 'H-1B1 USCIS';

      case VisaStatus.H_1B1_STATUS:
        return 'H-1B1';

      case VisaStatus.H_1B1_CONSULATE:
        return 'H-1B1 Consulate';

      case VisaStatus.E_3:
        return 'E-3 USCIS';

      case VisaStatus.E_3_STATUS:
        return 'E-3';

      case VisaStatus.E_3D:
        return 'E-3D USCIS';

      case VisaStatus.E_3D_STATUS:
        return 'E-3D';

      case VisaStatus.E_3_CONSULATE:
        return 'E-3 Consulate';

      case VisaStatus.E_3D_CONSULATE:
        return 'E-3D Consulate';

      case VisaStatus.PERM_GREEN_CARD:
        return 'PERM: Green Card';

      case VisaStatus.H_3:
        return 'H-3';

      case VisaStatus.H_4:
        return 'H-4 USCIS';

      case VisaStatus.H_4_STATUS:
        return 'H-4';

      case VisaStatus.H4_CONSULATE:
        return 'H-4 Consulate';

      case VisaStatus.I:
        return 'I';

      case VisaStatus.J_1:
        return 'J-1';

      case VisaStatus.L_1:
        return 'L-1';

      case VisaStatus.L_2:
        return 'L-2';

      case VisaStatus.L_1A:
        return 'L-1A';

      case VisaStatus.L_1B:
        return 'L-1B';

      case VisaStatus.L_BLANKET:
        return 'L-Blanket';

      case VisaStatus.LPR:
        return 'LPR';

      case VisaStatus.O_1:
        return 'O-1';

      case VisaStatus.O_3:
        return 'O-3';

      case VisaStatus.P_1:
        return 'P-1';

      case VisaStatus.P_3:
        return 'P-3';

      case VisaStatus.R_1:
        return 'R-1';

      case VisaStatus.TN:
        return 'TN USCIS';

      case VisaStatus.TN_STATUS:
        return 'TN';

      case VisaStatus.TN_MEXICO:
        return 'TN Mexico';

      case VisaStatus.TN_CANADA:
        return 'TN Canada';

      case VisaStatus.TD:
        return 'TD USCIS';

      case VisaStatus.TD_STATUS:
        return 'TD';

      case VisaStatus.TD_MEXICO:
        return 'TD Mexico';

      case VisaStatus.TD_CANADA:
        return 'TD Canada';

      case VisaStatus.US_CITIZEN:
        return 'U.S. Citizen';

      case VisaStatus.NOT_IN_US:
        return 'Not in U.S.';

      case VisaStatus.AOS_APPLICANT:
        return 'AOS applicant';

      case VisaStatus.USCIS_DEPENDENT:
        return 'USCIS Dependent';

      case VisaStatus.NOT_SURE:
        return 'Not sure';

      case VisaStatus.USCIS_PRIMARY:
        return 'USCIS Primary - Company';

      case VisaStatus.USCIS_PRIMARY_2:
        return 'USCIS Primary - Individual';

      case VisaStatus.EAD_PRIMARY:
        return 'EAD Primary';

      case VisaStatus.EAD_DEPENDENT:
        return 'EAD Dependent ';

      case VisaStatus.H_1B_LOTTERY:
        return 'H-1B Lottery \'23 ';

      case VisaStatus.H_1B_24_LOTTERY:
        return 'H-1B Lottery \'24 ';

      case VisaStatus.H_1B_25_LOTTERY:
        return 'H-1B Lottery \'25 ';

      case VisaStatus.H_1B_26_LOTTERY:
        return 'H-1B Lottery \'26 ';

      case VisaStatus.A_1:
        return 'A-1';

      case VisaStatus.A_2:
        return 'A-2';

      case VisaStatus.A_3:
        return 'A-3';

      case VisaStatus.ASYLEE:
        return 'Asylee';

      case VisaStatus.C_1:
        return 'C-1';

      case VisaStatus.CW_1:
        return 'CW-1';

      case VisaStatus.CW_2:
        return 'CW-2';

      case VisaStatus.D:
        return 'D';

      case VisaStatus.DACA:
        return 'DACA';

      case VisaStatus.E_1D:
        return 'E-1D';

      case VisaStatus.E_2D:
        return 'E-2D';

      case VisaStatus.ESTA:
        return 'ESTA';

      case VisaStatus.G_1:
        return 'G-1';

      case VisaStatus.G_2:
        return 'G-2';

      case VisaStatus.G_3:
        return 'G-3';

      case VisaStatus.G_4:
        return 'G-4';

      case VisaStatus.G_5:
        return 'G-5';

      case VisaStatus.H_2B:
        return 'H-2B';

      case VisaStatus.M_1:
        return 'M-1';

      case VisaStatus.M_2:
        return 'M-2';

      case VisaStatus.NATO_1:
        return 'NATO-1';

      case VisaStatus.NATO_2:
        return 'NATO-2';

      case VisaStatus.NATO_3:
        return 'NATO-3';

      case VisaStatus.NATO_4:
        return 'NATO-4';

      case VisaStatus.NATO_5:
        return 'NATO-5';

      case VisaStatus.NATO_6:
        return 'NATO-6';

      case VisaStatus.NATO_7:
        return 'NATO-7';

      case VisaStatus.O_2:
        return 'O-2';

      case VisaStatus.OTHER:
        return 'Other';

      case VisaStatus.P_2:
        return 'P-2';

      case VisaStatus.P_4:
        return 'P-4';

      case VisaStatus.Q_1:
        return 'Q-1';

      case VisaStatus.PAROLEE:
        return 'Parolee';

      case VisaStatus.PENDING_ASYLEE:
        return 'Pending Asylee';

      case VisaStatus.R_2:
        return 'R-2';

      case VisaStatus.U_1:
        return 'U-1';

      case VisaStatus.U_2:
        return 'U-2';

      case VisaStatus.U_3:
        return 'U-3';

      case VisaStatus.U_4:
        return 'U-4';

      case VisaStatus.U_5:
        return 'U-5';

      case VisaStatus.T_1:
        return 'T-1';

      case VisaStatus.T_2:
        return 'T-2';

      case VisaStatus.T_3:
        return 'T-3';

      case VisaStatus.T_4:
        return 'T-4';

      case VisaStatus.TPS:
        return 'TPS';

      case VisaStatus.J_2:
        return 'J-2';

      case VisaStatus.K_1:
        return 'K-1';

      case VisaStatus.K_3:
        return 'K-3';

      case VisaStatus.GLOBAL_PROCESS:
        return 'Global Process';

      default:
        return e;
    }
  },
  VisaAdditionalProcessing: (e: VisaAdditionalProcessing) => {
    switch (e) {
      case VisaAdditionalProcessing.EAD:
        return 'EAD';

      case VisaAdditionalProcessing.AP:
        return 'AP';

      default:
        return e;
    }
  },
  VisaSubCategory: (e: VisaSubCategory) => {
    switch (e) {
      case VisaSubCategory.H_1B_CHANGE_STATUS:
      case VisaSubCategory.H_4_CHANGE_STATUS:
        return 'Change of Status';

      case VisaSubCategory.H_1B_EXTENSION:
      case VisaSubCategory.H_4_EXTENSION:
        return 'Extension';

      case VisaSubCategory.H_1B_EXTENSION_SUBSEQUENT:
        return 'Extension (subsequent)';

      case VisaSubCategory.H_1B_AMENDMENT:
        return 'Amendment';

      case VisaSubCategory.H_1B_TRANSFER:
        return 'Transfer/change of Employment';

      case VisaSubCategory.H_1B_CAP_LOTTERY:
        return 'Cap Lottery';

      case VisaSubCategory.USCIS_PRIMARY_ADVANCE_PAROLE:
        return 'Advance Parole';

      case VisaSubCategory.USCIS_PRIMARY_B_1:
        return 'B-1';

      case VisaSubCategory.USCIS_PRIMARY_EB_1A:
        return 'EB-1A Extraordinary Ability';

      case VisaSubCategory.USCIS_PRIMARY_EB_1B:
        return 'EB-1B Outstanding Professor and Researcher';

      case VisaSubCategory.USCIS_PRIMARY_EB_1C:
        return 'EB-1C Multinational Manager or Executive';

      case VisaSubCategory.USCIS_PRIMARY_E_1:
        return 'E-1';

      case VisaSubCategory.USCIS_PRIMARY_E_2:
        return 'E-2';

      case VisaSubCategory.USCIS_PRIMARY_H_3:
        return 'H-3';

      case VisaSubCategory.USCIS_PRIMARY_I:
        return 'I';

      case VisaSubCategory.USCIS_PRIMARY_INTERNATIONAL_ENTREPRENEUR_PAROLE:
        return 'International Entrepreneur Parole';

      case VisaSubCategory.USCIS_PRIMARY_I_485J:
        return 'I-485J';

      case VisaSubCategory.USCIS_PRIMARY_I_140:
        return 'I-140';

      case VisaSubCategory.USCIS_PRIMARY_J_1:
        return 'J-1';

      case VisaSubCategory.USCIS_PRIMARY_L_BLANKET:
        return 'L-Blanket';

      case VisaSubCategory.USCIS_PRIMARY_L_1A:
        return 'L-1A';

      case VisaSubCategory.USCIS_PRIMARY_L_1B:
        return 'L-1B';

      case VisaSubCategory.USCIS_PRIMARY_NATIONAL_INTEREST_WAIVER:
        return 'National Interest Waiver';

      case VisaSubCategory.USCIS_PRIMARY_N_400:
        return 'N-400 Citizenship';

      case VisaSubCategory.USCIS_PRIMARY_O_1:
        return 'O-1';

      case VisaSubCategory.USCIS_PRIMARY_O_2:
        return 'O-2';

      case VisaSubCategory.USCIS_PRIMARY_P_1:
        return 'P-1';

      case VisaSubCategory.USCIS_PRIMARY_P_3:
        return 'P-3';

      case VisaSubCategory.USCIS_PRIMARY_Q_1:
        return 'Q-1';

      case VisaSubCategory.USCIS_PRIMARY_R_1:
        return 'R-1';

      case VisaSubCategory.USCIS_PRIMARY_RE_ENTRY_PERMIT:
        return 'Re-Entry Permit';

      case VisaSubCategory.USCIS_PRIMARY_SCHEDULE_A_GREEN_CARD:
        return 'Schedule A Green Card';

      case VisaSubCategory.USCIS_DEPENDENT_ADVANCE_PAROLE:
        return 'Advance Parole';

      case VisaSubCategory.USCIS_DEPENDENT_B_1:
        return 'B-1';

      case VisaSubCategory.USCIS_DEPENDENT_B_2:
        return 'B-2';

      case VisaSubCategory.USCIS_DEPENDENT_DACA:
        return 'DACA';

      case VisaSubCategory.USCIS_DEPENDENT_E_1_DEPENDENT:
        return 'E-1 Dependent';

      case VisaSubCategory.USCIS_DEPENDENT_E_2_DEPENDENT:
        return 'E-2 Dependent';

      case VisaSubCategory.USCIS_DEPENDENT_EB_5_INVESTOR_I_526:
        return 'EB-5 Investor I-526';

      case VisaSubCategory.USCIS_DEPENDENT_EB_5_INVESTOR_I_829:
        return 'EB-5 Investor I-829';

      case VisaSubCategory.USCIS_DEPENDENT_F_1:
        return 'F-1';

      case VisaSubCategory.USCIS_DEPENDENT_F_2:
        return 'F-2';

      case VisaSubCategory.USCIS_DEPENDENT_I_130:
        return 'I-130';

      case VisaSubCategory.USCIS_DEPENDENT_I_140:
        return 'I-140';

      case VisaSubCategory.USCIS_DEPENDENT_I_751:
        return 'I-751';

      case VisaSubCategory.USCIS_DEPENDENT_INTENATIONAL_ENTREPRENEUR_PAROLE_DEPENDENT:
        return 'International Entrepreneur Parole Dependent';

      case VisaSubCategory.USCIS_DEPENDENT_J_2:
        return 'J-2';

      case VisaSubCategory.USCIS_DEPENDENT_K_1:
        return 'K-1';

      case VisaSubCategory.USCIS_DEPENDENT_L_2:
        return 'L-2';

      case VisaSubCategory.USCIS_DEPENDENT_N_400_CITIZENSHIP:
        return 'N-400 Citizenship';

      case VisaSubCategory.USCIS_DEPENDENT_O_3:
        return 'O-3';

      case VisaSubCategory.USCIS_DEPENDENT_P_4:
        return 'P-4';

      case VisaSubCategory.USCIS_DEPENDENT_Q_2:
        return 'Q-2';

      case VisaSubCategory.USCIS_DEPENDENT_REENTRY_PERMIT:
        return 'Re-entry Permit';

      case VisaSubCategory.USCIS_DEPENDENT_R_2:
        return 'R-2';

      case VisaSubCategory.USCIS_DEPENDENT_TEMPORARY_PROTECTED_STATUS:
        return 'Temporary Protected Status';

      case VisaSubCategory.H_1B_LOTTERY_CONSULAR_NOTIFICATION:
        return 'Consular Notification';

      case VisaSubCategory.H_1B_LOTTERY_CHANGE_OF_STATUS:
        return 'Change of Status';

      case VisaSubCategory.H_1B_LOTTERY_24_CONSULAR_NOTIFICATION:
        return 'Consular Notification';

      case VisaSubCategory.H_1B_LOTTERY_24_CHANGE_OF_STATUS:
        return 'Change of Status';

      case VisaSubCategory.H_1B_LOTTERY_25_CONSULAR_NOTIFICATION:
        return 'Consular Notification';

      case VisaSubCategory.H_1B_LOTTERY_25_CHANGE_OF_STATUS:
        return 'Change of Status';

      case VisaSubCategory.H_1B_LOTTERY_26_CONSULAR_NOTIFICATION:
        return 'Consular Notification';

      case VisaSubCategory.H_1B_LOTTERY_26_CHANGE_OF_STATUS:
        return 'Change of Status';

      // USCIS_PRIMARY_2
      case VisaSubCategory.USCIS_PRIMARY_2_ADVANCE_PAROLE:
        return 'Advance Parole';

      case VisaSubCategory.USCIS_PRIMARY_2_B_1:
        return 'B-1';

      case VisaSubCategory.USCIS_PRIMARY_2_B_2:
        return 'B-2';

      case VisaSubCategory.USCIS_PRIMARY_2_CONSULAR_PROCESSING:
        return 'Consular Processing';

      case VisaSubCategory.USCIS_PRIMARY_2_EB_1A:
        return 'EB-1A Extraordinary Ability';

      case VisaSubCategory.USCIS_PRIMARY_2_F_1:
        return 'F-1';

      case VisaSubCategory.USCIS_PRIMARY_2_I_130:
        return 'I-130';

      case VisaSubCategory.USCIS_PRIMARY_2_I_485J:
        return 'I-485J';

      case VisaSubCategory.USCIS_PRIMARY_2_K_1:
        return 'K-1';

      case VisaSubCategory.USCIS_PRIMARY_2_NATIONAL_INTEREST_WAIVER:
        return 'National Interest Waiver';

      case VisaSubCategory.USCIS_PRIMARY_2_N_400:
        return 'N-400 Citizenship';

      case VisaSubCategory.USCIS_PRIMARY_2_O_1:
        return 'O-1';

      case VisaSubCategory.USCIS_PRIMARY_2_RE_ENTRY_PERMIT:
        return 'Re-Entry Permit';

      case VisaSubCategory.USCIS_PRIMARY_2_U:
        return 'U';

      case VisaSubCategory.GLOBAL_PROCESS:
        return 'Global';

      case VisaSubCategory.H_1B_LOTTERY_MASTERS:
        return 'Masters';

      default:
        return e;
    }
  },
};

export enum UpcomingExpirations {
  ALL_EXPIRATIONS = 'ALL_EXPIRATIONS',
  EXPIRING_WITHIN_7_MO = 'EXPIRING_WITHIN_7_MO',
  EXPIRING_WITHIN_3_MO = 'EXPIRING_WITHIN_3_MO',
  EXPIRING_WITHIN_1_MO = 'EXPIRING_WITHIN_1_MO',
}

export enum ReportOptions {
  ALL_CLIENTS = 'ALL_CLIENTS',
  ONLY_MY_CLIENTS = 'ONLY_MY_CLIENTS',
}

export enum reportingFieldTypes {
  DATE = 'date',
  STRING = 'string',
  NUMBER = 'number',
  BOOLEAN = 'boolean',
  ENUM = 'enum',
  CONTRIBUTORS = 'contributors',
  SPONSOR = 'sponsor',
  MANAGER = 'manager',
  ATTORNEY = 'attorney',
  PARALEGAL = 'paralegal',
  COMPANY = 'company',
  YEAR = 'year',
}

export enum TextFilters {
  CONTAINS = 'contains',
  DOES_NOT_CONTAINS = 'notContains',
  EQUALS = 'eq',
  DOES_NOT_EQUAL = 'ne',
}

export enum NumberFilters {
  LESS_THAN = 'lt',
  GREATER_THAN = 'gt',
  LESS_THAN_EQUAL_TO = 'lte',
  GREATER_THAN_EQUAL_TO = 'gte',
  EQUAL_TO = 'eq',
  NOT_EQUAL_TO = 'ne',
}

export const numberFilterResolver = (filter: NumberFilters) => {
  switch (filter) {
    case NumberFilters.LESS_THAN:
      return 'less than';
    case NumberFilters.GREATER_THAN:
      return 'greater than';
    case NumberFilters.LESS_THAN_EQUAL_TO:
      return 'less than or equal to';
    case NumberFilters.GREATER_THAN_EQUAL_TO:
      return 'greater than or equal to';
    case NumberFilters.EQUAL_TO:
      return 'is equal to';
    case NumberFilters.NOT_EQUAL_TO:
      return 'is not equal to';
    default:
      return filter;
  }
};

export enum BooleanFilter {
  YES = 'eq',
  NO = 'ne',
}

export enum DateOperator {
  IS = 'IS',
  IS_WITHIN = 'IS_WITHIN',
  IS_BEFORE = 'IS_BEFORE',
  IS_AFTER = 'IS_AFTER',
  IS_NOT = 'IS_NOT',
}

export const dateOperatorResolver = (operator: DateOperator) => {
  switch (operator) {
    case DateOperator.IS:
      return 'is';
    case DateOperator.IS_WITHIN:
      return 'is within';
    case DateOperator.IS_BEFORE:
      return 'is before';
    case DateOperator.IS_AFTER:
      return 'is after';
    case DateOperator.IS_NOT:
      return 'is not';
    default:
      return operator;
  }
};

export enum WithinSubOperator {
  PAST_DAYS = 'PAST_DAYS',
  NEXT_DAYS = 'NEXT_DAYS',
  PAST_MONTHS = 'PAST_MONTHS',
  NEXT_MONTHS = 'NEXT_MONTHS',
  EXACT_DATE_RANGE = 'EXACT_DATE_RANGE',
}

export enum DateFilter {
  TODAY = 'TODAY',
  DAYS_AGO = 'DAYS_AGO',
  DAYS_FROM_NOW = 'DAYS_FROM_NOW',
  MONTHS_AGO = 'MONTHS_AGO',
  MONTHS_FROM_NOW = 'MONTHS_FROM_NOW',
  EXACT_DATE = 'EXACT_DATE',
}

export const SubOperatorResolver = (operator: WithinSubOperator | DateFilter) => {
  if ((operator as DateFilter) in DateFilter) {
    switch (operator) {
      case DateFilter.TODAY:
        return 'today';
      case DateFilter.DAYS_AGO:
        return 'number of days ago';
      case DateFilter.DAYS_FROM_NOW:
        return 'number of days from now';
      case DateFilter.MONTHS_AGO:
        return 'number of months ago';
      case DateFilter.MONTHS_FROM_NOW:
        return 'number of months from now';
      case DateFilter.EXACT_DATE:
        return 'exact date';
      default:
        return operator;
    }
  } else {
    switch (operator) {
      case WithinSubOperator.PAST_DAYS:
        return 'the past number of days';
      case WithinSubOperator.NEXT_DAYS:
        return 'the next number of days';
      case WithinSubOperator.PAST_MONTHS:
        return 'the past number of months';
      case WithinSubOperator.NEXT_MONTHS:
        return 'the next number of months';
      case WithinSubOperator.EXACT_DATE_RANGE:
        return 'exact date range';
      default:
        return operator;
    }
  }
};

export enum CustomDateRangeFilter {
  DAYS = 'days',
  MONTHS = 'months',
}

export enum CapType {
  BACHELORS = 'Bachelor’s Degree',
  MASTERS = 'U.S. Master’s Degree or Higher',
}

export const booleanFilterResolver = (filter: BooleanFilter) => {
  switch (filter) {
    case BooleanFilter.YES:
      return 'Yes';
    case BooleanFilter.NO:
      return 'No';
    default:
      return filter;
  }
};

export const filterResolver = (filter: TextFilters) => {
  switch (filter) {
    case TextFilters.CONTAINS:
      return 'contains';
    case TextFilters.DOES_NOT_CONTAINS:
      return 'does not contain';
    case TextFilters.EQUALS:
      return 'is';
    case TextFilters.DOES_NOT_EQUAL:
      return 'is not';
    default:
      return filter;
  }
};

export const selectionStatusResolver = (status: string) => {
  switch (status) {
    case 'selected':
      return 'Selected';
    case 'not-selected':
      return 'Not Selected';
    case 'awaiting-selection-notice':
      return 'Awaiting Selection Notice';
    default:
      return status;
  }
};

export const casePreparationResolver = (prep: string) => {
  switch (prep) {
    case 'lottery-submission-only':
      return 'Lottery Submission Only';
    case 'full-preparation':
      return 'Full Preparation';
    case 'to-be-determined':
      return 'To Be Determined';
    default:
      return prep;
  }
};

export enum AccessPermission {
  ONLY_ME = 'ONLY_ME',
  TEAM_AND_SELECTED_COMPANIES = 'TEAM_AND_SELECTED_COMPANIES',
  TEAM_AND_ALL_COMPANIES = 'TEAM_AND_ALL_COMPANIES',
  UNSET = 'UNSET',
}

export enum EditPermission {
  ONLY_MH_ADMINS = 'ONLY_MH_ADMINS',
  ONLY_MH_TEAM = 'ONLY_MH_TEAM',
  TEAM_AND_ALL_SELECTED_COMPANIES = 'TEAM_AND_ALL_SELECTED_COMPANIES',
  ONLY_ME = 'ONLY_ME',
  UNSET = 'UNSET',
}

export const accessResolver = (accessType: AccessPermission) => {
  switch (accessType) {
    case AccessPermission.ONLY_ME:
      return 'Only me';
    case AccessPermission.TEAM_AND_SELECTED_COMPANIES:
      return 'MH Team and Select Companies';
    case AccessPermission.TEAM_AND_ALL_COMPANIES:
      return 'MH Team and All Companies';
    default:
      return accessType;
  }
};

export const editAccessResolver = (accessType: EditPermission) => {
  switch (accessType) {
    case EditPermission.ONLY_MH_ADMINS:
      return 'Only MH Admins';
    case EditPermission.ONLY_MH_TEAM:
      return 'Only MH Team';
    case EditPermission.TEAM_AND_ALL_SELECTED_COMPANIES:
      return 'MH Team and All Selected Companies';

    default:
      return accessType;
  }
};

export enum StartWith {
  USE_TEMPLATE = 'Use Template',
  BLANK_REPORT = 'Start from Scratch',
}

export enum TemplateCategory {
  ForeignNational = 'ForeignNational',
  Case = 'Case',
  Company = 'Company',
  Employer = 'Employer',
}

export const TemplateCategoryResolver = (category: TemplateCategory) => {
  switch (category) {
    case TemplateCategory.Case:
      return 'Cases';
    case TemplateCategory.ForeignNational:
      return 'Foreign National';
    case TemplateCategory.Company:
      return 'Company';
    case TemplateCategory.Employer:
      return 'Employer';
    default: return category;
  }
};

// key Filing Dates entity is missing kindly replace it with '' down here once discovered.
export type Entity = 'ForeignNational'
| 'Case'
| 'FileData'
| 'Custom'
| 'ProcessRequest'
| 'Company'
| 'JobDescription'
| ''
| 'ProcessRequestEmploymentData';

export enum TimeUnit {
  WEEKS = 'WEEKS',
  MONTHS = 'MONTHS',
  YEARS = 'YEARS',
}

export const TimeUnitResolver = (unit: TimeUnit) => {
  switch (unit) {
    case TimeUnit.WEEKS:
      return 'weeks';
    case TimeUnit.MONTHS:
      return 'months';
    case TimeUnit.YEARS:
      return 'years';
    default: return unit;
  }
};
export enum ExecMode {
  RUN_ONLY = 'RUN_ONLY',
  SCHEDULE_ONLY = 'SCHEDULER_ONLY',
  RUN_AND_SCHEDULE = 'RUN_AND_SCHEDULE',
  SAVE_ONLY = 'SAVE_ONLY',
  SAVE_AND_RUN = 'SAVE_AND_RUN',
}

export enum DeleteType {
  SOFT_DELETE = 'SOFT_DELETE',
  HARD_DELETE = 'HARD_DELETE',
}

export enum InviteStatus {
  INVITE = 'INVITE',
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  DISABLED = 'DISABLED',
}

export const inviteStatusResolver = (code: InviteStatus) => {
  switch (code) {
    case InviteStatus.INVITE:
      return 'Not Invited';
    case InviteStatus.PENDING:
      return 'Pending';
    case InviteStatus.ACCEPTED:
      return 'Accepted';
    case InviteStatus.DISABLED:
      return 'Disabled';

    default:
      return code;
  }
};

export enum CompanyContactLabel {
  TEAM_MEMBER = 'TEAM_MEMBER',
  INVOICING_CONTACT = 'INVOICING_CONTACT',
  PRIMARY_CONTACT = 'PRIMARY_CONTACT',
  SENIOR_CONTACT = 'SENIOR_CONTACT',
  SIGNATORY_CONTACT = 'SIGNATORY_CONTACT',
}

export const contactLabelResolver = (contactLabel: CompanyContactLabel) => {
  switch (contactLabel) {
    case CompanyContactLabel.TEAM_MEMBER:
      return 'Team Member';
    case CompanyContactLabel.PRIMARY_CONTACT:
      return 'Primary Contact';
    case CompanyContactLabel.INVOICING_CONTACT:
      return 'Invoicing Contact';
    case CompanyContactLabel.SENIOR_CONTACT:
      return 'Senior Contact';
    case CompanyContactLabel.SIGNATORY_CONTACT:
      return 'Signatory Contact';

    default:
      return contactLabel;
  }
};

export enum TASK_VISIBILITY {
  ALL = 'All',
  EM = 'EM',
  FN = 'FN',
  MH = 'MH',
}

export const taskVisibilityResolver = (groupsCanRead: string[]): string => {
  const isEMVisible = groupsCanRead.includes('Employers');
  const isFNVisible = groupsCanRead.includes('Nationals');

  switch (true) {
    case isEMVisible && isFNVisible:
      return TASK_VISIBILITY.ALL;
    case isEMVisible:
      return TASK_VISIBILITY.EM;
    case isFNVisible:
      return TASK_VISIBILITY.FN;
    default:
      return TASK_VISIBILITY.MH;
  }
};

export const receiptNoticeResolver = (receiptNotice: string | null): ReceiptType | null => {
  switch (receiptNotice) {
    case 'AOSReceiptNotice':
      return ReceiptType.AOS;
    case 'USCISReceiptNumber':
      return ReceiptType.USCIS;
    case 'I485JReceiptNotice':
      return ReceiptType.I485J;
    default:
      return null;
  }
};
